/**
 * ACT2-690: a question asked in a workspace with no warehouse connection waits here, so chat can offer
 * to connect one first, instead of the agent running without a warehouse and failing to answer.
 *
 * The signal is the Settings → Warehouses connection list — the same "any connection" test the server
 * uses for welcome suggestions (`hasWarehouse` in `src/services/welcome-suggestions.ts`). Only a
 * loaded, empty list holds a message: while it loads or after it fails, chat behaves as before, so an
 * unknown never blocks anyone from sending.
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';

export interface HeldMessage {
  content: string;
  invokedSkillIds?: string[];
}

export interface WarehouseGate {
  held: HeldMessage | null;
  /** Use in place of `send` at every entry point. */
  gatedSend: (content: string, invokedSkillIds?: string[]) => void;
  /** Sends the held message now, after a connection is made. */
  release: () => Promise<void>;
  /** Sends the held message and stops holding for the rest of the browser session in this workspace. */
  sendAnyway: () => Promise<void>;
  /** Drops the held message and puts it back in the composer. */
  dismiss: () => void;
}

export interface WarehouseGateOptions {
  /** Puts text back in the composer. */
  restore: (content: string) => void;
  /** What a held message belongs to, such as the chat id. A held message is dropped when it changes. */
  scope?: string;
  /** Send straight through, such as while a turn runs: a hold would cover the composer's Stop button. */
  bypass?: boolean;
}

export function useWarehouseGate(
  send: (content: string, invokedSkillIds?: string[]) => Promise<void>,
  { restore, scope, bypass = false }: WarehouseGateOptions,
): WarehouseGate {
  const orpc = useOrpc();
  // `isSuccess`, not just `data`: a failed refetch keeps the last list, and an error must send through.
  const { data, isSuccess } = useQuery(orpc.settings.warehouses.listAccounts.queryOptions());
  // Once someone chose to send without a warehouse, asking again on every message is just friction —
  // most of all for a member, who can never connect one.
  const { slug } = useWorkspace();
  const skipKey = `lookout.warehouseGate.skipped.${slug}`;
  const [skipped, setSkipped] = useState(() => readSkipped(skipKey));
  const noWarehouse = isSuccess && data.accounts.length === 0 && !skipped;

  const [held, setHeld] = useState<HeldMessage | null>(null);
  // Mirrors `held` so a second release in the same tick (connect and the new-connection watcher can
  // both fire before a re-render) finds it already taken and sends nothing.
  const heldRef = useRef<HeldMessage | null>(null);
  const hold = (message: HeldMessage | null): void => {
    heldRef.current = message;
    setHeld(message);
  };
  // The view can stay mounted across chats, and a question asked in one must not be sent into another.
  useEffect(
    () => () => {
      heldRef.current = null;
      setHeld(null);
    },
    [scope],
  );

  const release = async (): Promise<void> => {
    const message = heldRef.current;
    if (message === null) return;
    hold(null);
    await send(message.content, message.invokedSkillIds);
  };

  return {
    held,
    gatedSend: (content, invokedSkillIds) => {
      if (heldRef.current !== null) return;
      if (noWarehouse && !bypass) hold({ content, invokedSkillIds });
      else void send(content, invokedSkillIds);
    },
    release,
    sendAnyway: () => {
      setSkipped(true);
      try {
        sessionStorage.setItem(skipKey, '1');
      } catch {
        // Storage blocked: the skip still holds until this view unmounts.
      }
      return release();
    },
    dismiss: () => {
      const message = heldRef.current;
      hold(null);
      if (message !== null) restore(message.content);
    },
  };
}

function readSkipped(key: string): boolean {
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}
